import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ExternalLink, GitCompare, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/i18n";
import { Comparison, getComparison } from "../../services/Comparisons";
import ContinueInChatButton from "./ContinueInChatButton";
import { OVERALL, RISK_CHIP } from "./risk-styles";

/**
 * A comparison in the chat: progress while it runs, then the verdict and
 * risk counts, with links to the full result.
 */
const ComparisonCard: React.FC<{ comparisonId: string }> = ({ comparisonId }) => {
  const { t } = useI18n();
  const c = t.comparisons;
  const [data, setData] = useState<Comparison | null>(null);
  const [gone, setGone] = useState(false);
  const loaded = useRef(false);

  const load = useCallback(async () => {
    const res = await getComparison(comparisonId);
    if (res.success && res.data) {
      loaded.current = true;
      setData(res.data);
    } else if (!loaded.current) {
      // Deleted, or the person lost access to the project
      setGone(true);
    }
  }, [comparisonId]);

  useEffect(() => {
    void load();
  }, [load]);

  const running = data?.status === "queued" || data?.status === "processing";
  useEffect(() => {
    if (!running) return;
    const timer = setInterval(() => void load(), 2500);
    return () => clearInterval(timer);
  }, [running, load]);

  const progress = data?.progress;
  const percent = progress && progress.total > 0 ? Math.min(100, Math.round((progress.done / progress.total) * 100)) : 0;

  return (
    <div className="rounded-lg border bg-card p-4 text-sm shadow-sm" data-testid="comparison-card">
      <div className="flex items-start gap-2">
        <GitCompare className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
        <div className="min-w-0 flex-1">
          {gone ? (
            <p className="text-muted-foreground">{c.cardUnavailable}</p>
          ) : !data ? (
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          ) : (
            <>
              <div className="font-medium">{c.cardTitle(data.standardName)}</div>
              <div className="truncate text-xs text-muted-foreground" title={data.receivedFileName}>
                {c.cardFile(data.receivedFileName)}
              </div>

              {running && (
                <div className="mt-3" role="status">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    <span>{progress ? c.stages[progress.stage] : c.statuses[data.status]}</span>
                    {progress && progress.total > 1 && (
                      <span className="text-xs">
                        {progress.done}/{progress.total}
                      </span>
                    )}
                  </div>
                  <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted">
                    <div className="h-full rounded-full bg-brand transition-all" style={{ width: `${Math.max(percent, 4)}%` }} />
                  </div>
                </div>
              )}

              {data.status === "failed" && (
                <div className="mt-3 flex items-start gap-2 text-red-700 dark:text-red-400">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>{data.error || c.cardFailed}</span>
                </div>
              )}

              {data.status === "ready" && (
                <div className="mt-3 space-y-2">
                  {data.summary?.overall && (
                    <div className={`font-semibold ${OVERALL[data.summary.overall]}`}>{c.overall[data.summary.overall]}</div>
                  )}
                  {data.summary?.headline && <p className="line-clamp-3">{data.summary.headline}</p>}
                  {data.summary?.counts && (
                    <div className="flex flex-wrap gap-1.5 text-xs font-medium">
                      {(["high", "medium", "low"] as const).map((r) => (
                        <span key={r} className={`rounded-full px-2 py-0.5 ${RISK_CHIP[r]}`}>
                          {c.risks[r]} {data.summary!.counts[r]}
                        </span>
                      ))}
                      <span className={`rounded-full px-2 py-0.5 ${RISK_CHIP.none}`}>
                        {c.kinds.match} {data.summary.counts.matches}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {data && !gone && (
        <div className="mt-3 flex flex-wrap gap-2 pl-6">
          <Button asChild size="sm" variant="default">
            <Link to={`/comparisons/${data.id}`}>
              <ExternalLink className="mr-2 h-4 w-4" />
              {c.openComparison}
            </Link>
          </Button>
          {data.status === "ready" && <ContinueInChatButton comparisonId={data.id} size="sm" />}
        </div>
      )}
    </div>
  );
};

export default ComparisonCard;
